import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Upload, FileSpreadsheet, AlertTriangle, ArrowRight, Camera } from "lucide-react";
import Expenses from "@/pages/Expenses";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, testid, warn }) => (
  <div className={`rounded-2xl border p-4 ${warn ? "border-[#F0C36D] bg-[#FFFBEB]" : "border-[#E3DED4] bg-white"}`}>
    <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{label}</div>
    <div className="font-serif text-2xl text-[#45423C] mt-1 tabular" data-testid={testid}>{value}</div>
  </div>
);

export default function ExpensesUpload() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [sp, setSp] = useSearchParams();
  const mode = !isOwner ? "receipts" : sp.get("mode") === "receipts" ? "receipts" : "softone";
  const setMode = (m) => { const n = new URLSearchParams(sp); m === "softone" ? n.delete("mode") : n.set("mode", m); setSp(n, { replace: true }); };
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const { data: imports = [] } = useQuery({ queryKey: ["exp-imports"], queryFn: async () => (await api.get("/expenses/imports")).data });
  const up = useMutation({
    mutationFn: async () => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post("/expenses/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setResult(d); setFile(null); if (ref.current) ref.current.value = "";
      qc.invalidateQueries();
      toast.success(`Διαβάστηκαν ${d.rows} γραμμές — ${d.new} νέες, ${d.updated} ενημερώθηκαν`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="space-y-6" data-testid="expenses-upload-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6F6A60]">Έξοδα</p>
        <h1 className="font-serif text-3xl text-[#45423C] mt-1">Ανέβασμα</h1>
        <p className="text-sm text-[#6F6A60] mt-1">Διαλέξτε τι ανεβάζετε: το Excel του SoftOne (παραστατικά για το P&L) ή αποδείξεις προσωπικού — από αρχείο ή με φωτογραφία/σάρωση.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="exp-upload-modes">
        {[["softone", FileSpreadsheet, "SoftOne Excel", "Αρχείο .xlsx «Αγορές/Παραστατικά» — ενημερώνει Κατηγορίες & P&L"], ["receipts", Camera, "Αποδείξεις Προσωπικού", "Ανέβασμα αρχείου ή σάρωση φωτογραφίας απόδειξης — για έγκριση & αποζημίωση"]].filter(([k]) => isOwner || k === "receipts").map(([k, Icon, t, s]) => (
          <button key={k} data-testid={`exp-mode-${k}`} onClick={() => setMode(k)}
            className={`text-left rounded-2xl border p-4 flex items-start gap-3 transition-colors ${mode === k ? "border-[#C86235] bg-[#FDF6F1]" : "border-[#E3DED4] bg-white hover:border-[#B9AE9A]"}`}>
            <Icon className={`h-6 w-6 shrink-0 ${mode === k ? "text-[#C86235]" : "text-[#6E7A63]"}`} />
            <div><div className="font-semibold text-[#45423C]">{t}</div><div className="text-xs text-[#6F6A60] mt-0.5">{s}</div></div>
          </button>
        ))}
      </div>

      {mode === "receipts" && <Expenses embedded />}
      {mode === "softone" && <>
      <p className="text-sm text-[#6F6A60]">Αρχείο .xlsx με στήλες «Εμπορ.κατ. Supplier», «Επωνυμία», «Καθαρή αξία», «Τύπος», «Παραστατικό», «Ημερ/νία». Ένα παραστατικό ανά γραμμή — τα ίδια παραστατικά ενημερώνονται, δεν διπλογράφονται, και δεν σβήνονται άλλοι μήνες.</p>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 flex flex-wrap items-center gap-4">
        <FileSpreadsheet className="h-8 w-8 text-[#6E7A63]" />
        <input data-testid="exp-upload-file" ref={ref} type="file" accept=".xlsx" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        <button data-testid="exp-upload-submit" disabled={!file || up.isPending} onClick={() => up.mutate()}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2F2D29] disabled:opacity-50">
          <Upload className="h-4 w-4" /> {up.isPending ? "Ανέβασμα…" : "Ανέβασμα"}
        </button>
      </div>

      {result && (
        <div className="space-y-3" data-testid="exp-upload-result">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Γραμμές" value={result.rows} testid="exp-res-rows" />
            <Stat label="Νέες" value={result.new} testid="exp-res-new" />
            <Stat label="Ενημερώθηκαν" value={result.updated} testid="exp-res-updated" />
            <Stat label="Σύνολο €" value={fmtEuro(result.total)} testid="exp-res-total" />
            <Stat label="Κατηγορίες χωρίς γραμμή P&L" value={result.unmapped_categories.length} testid="exp-res-unmapped" warn={result.unmapped_categories.length > 0} />
          </div>
          <div className="text-xs text-[#6F6A60]">{result.unchanged} αμετάβλητες · {result.credit_notes} πιστωτικά (αρνητική αξία) · {result.skipped} κενές γραμμές παραλείφθηκαν · έτη: {result.years.join(", ")}</div>
          {result.unmapped_categories.length > 0 && (
            <div className="rounded-2xl border border-[#F0C36D] bg-[#FFFBEB] p-4 flex flex-wrap items-center gap-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-[#92400E]" />
              <span className="text-[#92400E] font-medium">Χωρίς γραμμή P&L:</span> {result.unmapped_categories.join(", ")}
              <Link to="/expense-categories?status=unmapped" data-testid="exp-goto-unmapped" className="ml-auto inline-flex items-center gap-1 text-[#C86235] font-semibold hover:underline">Αντιστοίχιση <ArrowRight className="h-4 w-4" /></Link>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E3DED4] text-sm font-medium text-[#45423C]">Ιστορικό ανεβασμάτων</div>
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-4 py-2 text-left">Πότε</th><th className="px-4 text-left">Αρχείο</th><th className="px-4 text-left">Από</th><th className="px-4 text-right">Γραμμές</th><th className="px-4 text-right">Νέες</th><th className="px-4 text-right">Ενημ.</th><th className="px-4 text-right">Σύνολο</th></tr>
          </thead>
          <tbody>
            {imports.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-[#6F6A60]">Δεν έχει γίνει ακόμη ανέβασμα.</td></tr>}
            {imports.map((i) => (
              <tr key={i.id} className="border-t border-[#E3DED4]" data-testid={`exp-import-${i.id}`}>
                <td className="px-4 py-2 whitespace-nowrap">{new Date(i.at).toLocaleString("el-GR")}</td>
                <td className="px-4 truncate max-w-[260px]">{i.filename}</td>
                <td className="px-4">{i.by}</td>
                <td className="px-4 text-right tabular">{i.rows}</td>
                <td className="px-4 text-right tabular">{i.new}</td>
                <td className="px-4 text-right tabular">{i.updated}</td>
                <td className="px-4 text-right tabular">{fmtEuro(i.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
    </div>
  );
}
